import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Shape, SHAPE_COMPARABLE_FIELDS } from '../../model/types';

export interface ShapeChangesResult {
  addedElements: Shape[];
  removedElements: Shape[];
  updatedElements: Shape[];
}

const NUMERIC_PRECISION = 2;

const arePrimitivesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'number' && typeof b === 'number') {
    return roundNumber(a, NUMERIC_PRECISION) === roundNumber(b, NUMERIC_PRECISION);
  }
  return false;
};

const arePointArraysEqual = (a: unknown, b: unknown): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!arePrimitivesEqual(a[i], b[i])) return false;
  }
  return true;
};

const isShapeFieldEqual = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return arePointArraysEqual(a, b);
  }
  return arePrimitivesEqual(a, b);
};

const hasAnyTrackedFieldChanged = (initial: Shape, current: Shape): boolean => {
  if (initial.type !== current.type) return true;
  const fields = SHAPE_COMPARABLE_FIELDS[current.type] ?? [];
  const initialBag = initial as unknown as Record<string, unknown>;
  const currentBag = current as unknown as Record<string, unknown>;
  return fields.some((field) => !isShapeFieldEqual(initialBag[field], currentBag[field]));
};

export const checkShapesChanges = (initialShapes: Shape[], currentShapes: Shape[]): ShapeChangesResult => {
  const initialById = new Map(initialShapes.map((shape) => [shape.id, shape]));
  const currentById = new Map(currentShapes.map((shape) => [shape.id, shape]));

  const addedElements = currentShapes.filter((shape) => !initialById.has(shape.id));
  const removedElements = initialShapes.filter((shape) => !currentById.has(shape.id));

  const updatedElements = currentShapes.filter((current) => {
    const initial = initialById.get(current.id);
    if (!initial) return false;
    return hasAnyTrackedFieldChanged(initial, current);
  });

  return { addedElements, removedElements, updatedElements };
};
